import React, { useEffect, useState } from "react";
import { useLang } from "../LangContext";
import { useTenant } from "../TenantContext";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, Home, Layers, Calendar, Search, Filter, ExternalLink, Waves, Mountain, Trees } from "lucide-react";
import PublicImmobiliMap from "../components/PublicImmobiliMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stateColors = {
    "Disponibile": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "In Trattativa": "bg-amber-100 text-amber-800 border-amber-200",
    "Venduto": "bg-stone-200 text-stone-800 border-stone-300",
    "Riservato": "bg-blue-100 text-blue-800 border-blue-200",
};

const VISTA_META = {
    lago: { label: "Vista Lago", icon: Trees, cls: "bg-emerald-600" },
    mare: { label: "Vista Mare", icon: Waves, cls: "bg-sky-600" },
    montagna: { label: "Vista Montagna", icon: Mountain, cls: "bg-amber-700" },
};

export default function ImmobiliListPage() {
    const { lang } = useLang();
    const { slug } = useTenant();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [tipologia, setTipologia] = useState("");
    const [stato, setStato] = useState("");
    const [vistaFilters, setVistaFilters] = useState([]);   // ["lago","mare","montagna"]
    const [availableVistaFlags, setAvailableVistaFlags] = useState([]);

    useEffect(() => {
        window.scrollTo({ top: 0 });
        axios.get(`${API}/immobili`, { params: { borgo: slug } }).then((r) => setItems(r.data)).catch(() => {});
        // Tenant public config for amenity flags
        axios.get(`${API}/tenants/public/${slug}`)
            .then((r) => setAvailableVistaFlags(r.data?.available_vista_flags || ["lago", "mare", "montagna"]))
            .catch(() => setAvailableVistaFlags(["lago", "mare", "montagna"]));
    }, [slug]);

    const tipologie = Array.from(new Set(items.map(i => i.classificazione?.tipologia).filter(Boolean)));
    const stati = Array.from(new Set(items.map(i => i.stato_conservazione?.stato).filter(Boolean)));

    const filtered = items.filter((it) => {
        const denom = (it.anagrafica?.denominazione || "").toLowerCase();
        const via = (it.anagrafica?.via_piazza || "").toLowerCase();
        const cod = (it.codice || "").toLowerCase();
        const qq = q.toLowerCase();
        if (qq && !denom.includes(qq) && !via.includes(qq) && !cod.includes(qq)) return false;
        if (tipologia && it.classificazione?.tipologia !== tipologia) return false;
        if (stato && it.stato_conservazione?.stato !== stato) return false;
        // Vista filter: AND semantics — l'immobile deve avere TUTTI i flag richiesti
        if (vistaFilters.length) {
            const own = it.ubicazione?.vista_flags || [];
            for (const f of vistaFilters) if (!own.includes(f)) return false;
        }
        return true;
    });

    const toggleVista = (f) => setVistaFilters((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);

    return (
        <div className="pt-32 pb-24 px-6 lg:px-10 min-h-screen bg-stone-50" data-testid="immobili-list-page">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-10">
                    <div className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-700 mb-3">
                        ABITARE TADASUNI
                    </div>
                    <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-semibold text-stone-900">
                        {lang === "it" ? "Immobili in Vendita" : "Properties for Sale"}
                    </h1>
                    <p className="mt-4 text-lg text-stone-600 max-w-2xl mx-auto">
                        {lang === "it"
                            ? "Il fascicolo digitale delle unità immobiliari censite del borgo di Tadasuni."
                            : "The digital dossier of surveyed real-estate units in the village of Tadasuni."}
                    </p>
                </div>

                {/* Interactive map — all published properties with coordinates */}
                <div className="mb-10" data-testid="immobili-map-section">
                    <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                        <div>
                            <div className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-700 mb-1">
                                {lang === "it" ? "MAPPA INTERATTIVA" : "INTERACTIVE MAP"}
                            </div>
                            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-stone-900">
                                {lang === "it" ? "Esplora gli immobili sulla mappa" : "Explore properties on the map"}
                            </h2>
                        </div>
                        <div className="text-xs text-stone-500 flex items-center gap-1.5">
                            <MapPin size={13} className="text-emerald-600" />
                            {items.filter(i => typeof i?.ubicazione?.latitudine === "number" && typeof i?.ubicazione?.longitudine === "number").length}
                            {" "}
                            {lang === "it" ? "immobili georeferenziati" : "geo-tagged properties"}
                        </div>
                    </div>
                    <PublicImmobiliMap
                        items={items}
                        height="520px"
                        variant="full"
                        borgoSlug={slug}
                    />
                </div>

                {/* Filters */}
                <div className="bg-white border border-stone-200 rounded-3xl p-4 md:p-5 shadow-sm mb-10 flex flex-col md:flex-row items-stretch md:items-center gap-3" data-testid="immobili-filters">
                    <div className="flex-1 flex items-center gap-2 px-4 py-2 rounded-full bg-stone-50 border border-stone-200">
                        <Search size={16} className="text-stone-500" />
                        <input
                            type="text"
                            placeholder={lang === "it" ? "Cerca immobile..." : "Search property..."}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="flex-1 bg-transparent outline-none text-sm"
                            data-testid="immobili-search"
                        />
                    </div>
                    <select
                        value={tipologia}
                        onChange={(e) => setTipologia(e.target.value)}
                        className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm min-w-[180px]"
                        data-testid="filter-tipologia"
                    >
                        <option value="">{lang === "it" ? "Tutte le tipologie" : "All types"}</option>
                        {tipologie.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select
                        value={stato}
                        onChange={(e) => setStato(e.target.value)}
                        className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm min-w-[160px]"
                        data-testid="filter-stato"
                    >
                        <option value="">{lang === "it" ? "Tutti gli stati" : "All statuses"}</option>
                        {stati.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <div className="flex items-center gap-2 text-sm text-stone-500 px-2">
                        <Filter size={14} />
                        <span data-testid="immobili-count">{filtered.length}</span>
                    </div>
                </div>

                {/* Amenity chip filters */}
                {availableVistaFlags.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="immobili-vista-filters">
                        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider mr-2">
                            {lang === "it" ? "Caratteristiche:" : "Amenities:"}
                        </span>
                        {availableVistaFlags.map((f) => {
                            const meta = VISTA_META[f] || { label: f, icon: MapPin, cls: "bg-stone-500" };
                            const Icon = meta.icon;
                            const active = vistaFilters.includes(f);
                            return (
                                <button
                                    key={f}
                                    onClick={() => toggleVista(f)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${active ? `${meta.cls} text-white border-transparent` : "bg-white text-stone-700 border-stone-300 hover:border-emerald-400"}`}
                                    data-testid={`immobili-filter-vista-${f}`}
                                >
                                    <Icon size={12} /> {meta.label}
                                </button>
                            );
                        })}
                        {vistaFilters.length > 0 && (
                            <button
                                onClick={() => setVistaFilters([])}
                                className="ml-1 text-xs text-stone-500 hover:text-stone-800 underline"
                                data-testid="immobili-filter-vista-clear"
                            >
                                {lang === "it" ? "Pulisci" : "Clear"}
                            </button>
                        )}
                    </div>
                )}

                {/* Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {filtered.map((it, i) => {
                        const cover = it.marketing?.foto_copertina || it.marketing?.galleria?.[0] || "";
                        const stateCls = stateColors[it.stato_conservazione?.stato] || stateColors["Disponibile"];
                        return (
                            <Link
                                to={`/immobili/${it.id}`}
                                key={it.id}
                                className="card-hover bg-white border border-stone-200 rounded-2xl overflow-hidden block"
                                data-testid={`immobile-card-${i}`}
                            >
                                <div className="aspect-[16/10] overflow-hidden bg-emerald-50 relative">
                                    {cover ? (
                                        <img src={cover.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${cover}` : cover} alt={it.anagrafica?.denominazione} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Home size={40} className="text-emerald-300" />
                                        </div>
                                    )}
                                    <span className="absolute top-3 left-3 bg-emerald-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                                        {it.classificazione?.tipologia || "Immobile"}
                                    </span>
                                    <span className={`absolute top-3 right-3 border text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${stateCls}`}>
                                        {it.stato_conservazione?.stato || "Disponibile"}
                                    </span>
                                    {/* Vista badges */}
                                    {(it.ubicazione?.vista_flags || []).length > 0 && (
                                        <div className="absolute bottom-3 left-3 flex gap-1 flex-wrap max-w-[70%]" data-testid={`immobile-vista-badges-${i}`}>
                                            {(it.ubicazione.vista_flags || []).map((f) => {
                                                const meta = VISTA_META[f];
                                                if (!meta) return null;
                                                const Icon = meta.icon;
                                                return (
                                                    <span
                                                        key={f}
                                                        className={`inline-flex items-center gap-1 ${meta.cls} text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow`}
                                                        title={meta.label}
                                                    >
                                                        <Icon size={9} /> {f}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="p-5">
                                    <div className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1">{it.codice}</div>
                                    <h3 className="font-serif text-xl font-semibold text-stone-900 leading-tight mb-2 line-clamp-2">
                                        {it.anagrafica?.denominazione}
                                    </h3>
                                    <div className="flex items-center gap-1.5 text-stone-500 text-xs mb-3">
                                        <MapPin size={13} />
                                        {(it.anagrafica?.via_piazza || "") + ", " + (it.anagrafica?.comune || "Tadasuni")}
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-stone-700 pt-3 border-t border-stone-100">
                                        <span className="inline-flex items-center gap-1"><Layers size={12} className="text-amber-600" />{it.dimensioni?.mq_calpestabili || 0} mq</span>
                                        <span className="inline-flex items-center gap-1"><Home size={12} className="text-emerald-600" />{it.dimensioni?.vani || 0} vani</span>
                                        {it.dimensioni?.anno_costruzione && (
                                            <span className="inline-flex items-center gap-1"><Calendar size={12} className="text-stone-500" />{it.dimensioni.anno_costruzione}</span>
                                        )}
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between">
                                        <div className="font-serif text-lg font-semibold text-emerald-700">
                                            {it.marketing?.prezzo || "Su richiesta"}
                                        </div>
                                        <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-semibold">
                                            {lang === "it" ? "Scheda" : "Details"}
                                            <ExternalLink size={12} />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>

                {filtered.length === 0 && (
                    <div className="text-center py-16 text-stone-500" data-testid="no-immobili">
                        {lang === "it" ? "Nessun immobile trovato" : "No properties found"}
                    </div>
                )}
            </div>
        </div>
    );
}
